// src/pages/about/views/About.view.tsx
import React from "react";
import { useTranslation } from "react-i18next";
import Header from "../../../components/header/Header";
import { AboutViewProps } from "../types";
import Button from "../../../components/global/Button";
import LazyLoadedSection from "../../../components/lazyloadedsection/LazyLoadedSection.view";

import {
  AboutContainer,
  PageTitle,
  SectionTitle,
  AboutMeSection,
  AboutContent,
  AboutImageWrapper,
  Paragraph,
  Timeline,
  TimelineItem,
  TimelineContent,
  TimelineDot,
  TimelineTitle,
  TimelineSubtitle,
  TimelineDate,
  TimelineDescription,
  TechTags,
  TechTag,
  CertificationList,
  CertificationItem,
  CertificationInfo,
  CertificationName,
  CertificationIssuer,
  CertificationDate,
  CertificationLink,
  ButtonWrapper,
} from "../About.styled";

export function AboutView({
  isLoading,
  education,
  experience,
  aboutMeSections,
  certifications,
  handleDownloadCV,
}: AboutViewProps) {
  // Sem usar o hook de tradução por enquanto para evitar problemas de tipagem
  const { t } = useTranslation();
  return (
    <>
      <Header />
      <main>
        <AboutContainer>
          <PageTitle>{t("about:title")}</PageTitle>

          {aboutMeSections.map((section, index) => (
            <LazyLoadedSection key={index} visible={!isLoading}>
              <AboutMeSection>
                {index % 2 === 0 && section.image && (
                  <AboutImageWrapper>
                    <img src={section.image} alt={section.title} />
                  </AboutImageWrapper>
                )}

                <AboutContent>
                  <SectionTitle>
                    {section.title === "Quem sou eu"
                      ? t("about:whoAmI")
                      : section.title === "Filosofia de Trabalho"
                      ? t("about:workPhilosophy")
                      : section.title === "Fora do Trabalho"
                      ? t("about:outsideWork")
                      : section.title}
                  </SectionTitle>
                  {(() => {
                    const key =
                      section.title === "Quem sou eu"
                        ? "about:whoAmIContent"
                        : section.title === "Filosofia de Trabalho"
                        ? "about:workPhilosophyContent"
                        : section.title === "Fora do Trabalho"
                        ? "about:outsideWorkContent"
                        : null;

                    const paragraphs = key
                      ? (t(key, { returnObjects: true }) as string[])
                      : section.content;

                    return paragraphs.map((paragraph, i) => (
                      <Paragraph key={i}>{paragraph}</Paragraph>
                    ));
                  })()}
                </AboutContent>

                {index % 2 !== 0 && section.image && (
                  <AboutImageWrapper>
                    <img src={section.image} alt={section.title} />
                  </AboutImageWrapper>
                )}
              </AboutMeSection>
            </LazyLoadedSection>
          ))}

          <LazyLoadedSection visible={!isLoading}>
            <SectionTitle>{t("about:education")}</SectionTitle>
            <Timeline>
              {education.map(edu => (
                <TimelineItem key={edu.id}>
                  <TimelineDot />
                  <TimelineContent>
                    <TimelineTitle>{edu.institution}</TimelineTitle>
                    <TimelineSubtitle>
                      {edu.degree} {t("common:of", { defaultValue: "of" })} {edu.field}
                    </TimelineSubtitle>
                    <TimelineDate>{edu.period}</TimelineDate>
                    <TimelineDescription>{edu.description}</TimelineDescription>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </LazyLoadedSection>

          <LazyLoadedSection visible={!isLoading}>
            <SectionTitle>{t("about:experience")}</SectionTitle>
            <Timeline>
              {experience.map(exp => (
                <TimelineItem key={exp.id}>
                  <TimelineDot />
                  <TimelineContent>
                    <TimelineTitle>{exp.company}</TimelineTitle>
                    <TimelineSubtitle>{exp.role}</TimelineSubtitle>
                    <TimelineDate>{exp.period}</TimelineDate>
                    <TimelineDescription>{exp.description}</TimelineDescription>

                    <TechTags>
                      {exp.technologies.map((tech, index) => (
                        <TechTag key={index}>{tech}</TechTag>
                      ))}
                    </TechTags>
                  </TimelineContent>
                </TimelineItem>
              ))}
            </Timeline>
          </LazyLoadedSection>

          {/* Publicações removidas conforme solicitado */}

          <LazyLoadedSection visible={!isLoading}>
            <SectionTitle>{t("about:certifications")}</SectionTitle>
            <CertificationList>
              {certifications.map(cert => (
                <CertificationItem key={cert.id}>
                  <CertificationInfo>
                    <CertificationName>{cert.name}</CertificationName>
                    <CertificationIssuer>{cert.issuer}</CertificationIssuer>
                  </CertificationInfo>
                  <CertificationDate>{cert.date}</CertificationDate>
                  {cert.link && (
                    <CertificationLink
                      href={cert.link}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={t("common:readMore")}
                    >
                      <i className="ph ph-arrow-square-out"></i>
                    </CertificationLink>
                  )}
                </CertificationItem>
              ))}
            </CertificationList>
          </LazyLoadedSection>

          <ButtonWrapper>
            <Button onClick={handleDownloadCV} variant="primary">
              {t("common:downloadCV")}
            </Button>
          </ButtonWrapper>
        </AboutContainer>
      </main>
    </>
  );
}

export default AboutView;
